"use client";

import { useState } from "react";
import { DivergenceItem } from "@/types";
import { formatDate, getDivergenceSummary } from "./utils";

interface DivergencesTabProps {
  items: DivergenceItem[];
  filter: "all" | "bullish" | "bearish";
  setFilter: (filter: "all" | "bullish" | "bearish") => void;
  classFilter: "all" | "reversal" | "continuation";
  setClassFilter: (filter: "all" | "reversal" | "continuation") => void;
}

const chipBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.35rem",
  padding: "0.4rem 0.85rem",
  borderRadius: "1rem",
  fontSize: "0.82rem",
  fontWeight: "600",
  cursor: "pointer",
  border: "1px solid rgba(148, 163, 184, 0.25)",
  background: "rgba(15, 23, 42, 0.5)",
  color: "rgba(241, 245, 249, 0.65)",
  transition: "background 0.15s, color 0.15s, border-color 0.15s",
};

const chipActive: React.CSSProperties = {
  ...chipBase,
  background: "rgba(56, 189, 248, 0.15)",
  border: "1px solid rgba(56, 189, 248, 0.45)",
  color: "rgb(125, 211, 252)",
};

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" style={active ? chipActive : chipBase} onClick={onClick}>
      {label}
    </button>
  );
}

export default function DivergencesTab({
  items,
  filter,
  setFilter,
  classFilter,
  setClassFilter,
}: DivergencesTabProps) {
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);

  const getDivergenceDateValue = (item: DivergenceItem) => item.pivot_2_date || item.pivot_1_date || "";

  const getDivergenceTimestamp = (item: DivergenceItem) => {
    const parsed = Date.parse(getDivergenceDateValue(item));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const filteredItems = items
    .filter((item) => {
      if (filter === "bullish") return item.type?.includes("BULLISH");
      if (filter === "bearish") return item.type?.includes("BEARISH");
      return true;
    })
    .filter((item) => {
      if (classFilter === "reversal") return item.class === "reversal";
      if (classFilter === "continuation") return item.class === "continuation";
      return true;
    });

  const sortedItems = [...filteredItems].sort((a, b) => {
    const dateDiff = getDivergenceTimestamp(b) - getDivergenceTimestamp(a);
    if (dateDiff !== 0) return dateDiff;
    const aStrength = typeof a.strength_score === "number" ? a.strength_score : Number(a.strength_score ?? 0);
    const bStrength = typeof b.strength_score === "number" ? b.strength_score : Number(b.strength_score ?? 0);
    return bStrength - aStrength;
  });

  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      {/* Filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
        }}
      >
        <p style={{ color: "rgba(241, 245, 249, 0.6)", margin: 0, fontSize: "0.9rem" }}>
          {filteredItems.length} divergence{filteredItems.length !== 1 ? "s" : ""} found
        </p>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <FilterChip label="All" active={filter === "all"} onClick={() => setFilter("all")} />
          <FilterChip label="Bullish" active={filter === "bullish"} onClick={() => setFilter("bullish")} />
          <FilterChip label="Bearish" active={filter === "bearish"} onClick={() => setFilter("bearish")} />
          <span style={{ width: "1px", background: "rgba(148,163,184,0.2)", margin: "0 0.25rem" }} />
          <FilterChip label="All classes" active={classFilter === "all"} onClick={() => setClassFilter("all")} />
          <FilterChip label="Reversal" active={classFilter === "reversal"} onClick={() => setClassFilter("reversal")} />
          <FilterChip label="Continuation" active={classFilter === "continuation"} onClick={() => setClassFilter("continuation")} />
        </div>
      </div>

      {sortedItems.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <style>{`
            .div-row:hover { background: rgba(138, 180, 248, 0.08) !important; }
          `}</style>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              borderRadius: "0.5rem",
              overflow: "hidden",
            }}
          >
            <thead>
              <tr
                style={{
                  background: "rgba(138, 180, 248, 0.18)",
                  borderBottom: "2px solid var(--color-border)",
                }}
              >
                {["Symbol", "Divergence Date", "Type", "Strength", "RSI Zone", "Status", "Summary"].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "0.9rem 1rem",
                      textAlign: "left",
                      color: "var(--color-text)",
                      fontWeight: "600",
                      fontSize: "0.85rem",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item, idx) => {
                const isBullish = item.type?.includes("BULLISH");
                const isHidden = item.type?.includes("HIDDEN");
                const typeColor = isBullish ? "var(--google-green)" : "var(--google-red)";
                const typeBackground = isBullish ? "rgba(0, 200, 83, 0.15)" : "rgba(255, 82, 82, 0.15)";
                const divergenceClass = item.class === "continuation" ? "Continuation" : "Reversal";

                const strengthColors: Record<string, string> = {
                  strong: "var(--google-green)",
                  moderate: "#f59e0b",
                  weak: "var(--color-text-muted)",
                };
                const strengthColor = strengthColors[item.strength || "weak"] || "var(--color-text-muted)";

                const statusColors: Record<string, string> = {
                  confirmed: "var(--google-green)",
                  failed: "var(--google-red)",
                  developing: "#f59e0b",
                  too_recent: "var(--color-text-muted)",
                };
                const statusColor =
                  statusColors[item.confirmation_status || "developing"] || "var(--color-text-muted)";

                const zoneColors: Record<string, string> = {
                  overbought: "var(--google-red)",
                  oversold: "var(--google-green)",
                  bullish: "var(--google-green)",
                  bearish: "var(--google-red)",
                  neutral: "var(--color-text-muted)",
                };
                const zoneColor = zoneColors[item.rsi_zone || "neutral"] || "var(--color-text-muted)";

                const rowKey = `${item.symbol ?? ""}-${item.type ?? ""}-${item.pivot_2_date ?? idx}`;
                const divergenceDate = getDivergenceDateValue(item);

                return (
                  <tr
                    key={rowKey}
                    className="div-row"
                    style={{
                      borderBottom: "1px solid var(--color-border)",
                      background: idx % 2 === 0 ? "rgba(255, 255, 255, 0.02)" : "transparent",
                    }}
                  >
                    <td style={{ padding: "0.9rem 1rem", color: "var(--color-text)", fontWeight: "600" }}>
                      {item.symbol ? (
                        <a
                          href={`/stock/${encodeURIComponent(item.symbol)}`}
                          style={{ color: "var(--blue-text)", textDecoration: "none" }}
                        >
                          {item.symbol}
                        </a>
                      ) : (
                        "-"
                      )}
                      {item.conflicting_signals && (
                        <span
                          style={{ marginLeft: "0.5rem", color: "#f59e0b", fontSize: "0.75rem" }}
                          title="Conflicting signals detected"
                        >
                          ⚠️
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "0.9rem 1rem",
                        color: "var(--color-text)",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                      }}
                      title={
                        item.pivot_1_date && item.pivot_2_date
                          ? `Pivot range: ${formatDate(item.pivot_1_date) || item.pivot_1_date} to ${formatDate(item.pivot_2_date) || item.pivot_2_date}`
                          : undefined
                      }
                    >
                      {formatDate(divergenceDate) || "-"}
                    </td>
                    <td style={{ padding: "0.9rem 1rem" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "0.3rem 0.65rem",
                          borderRadius: "1rem",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          background: typeBackground,
                          color: typeColor,
                        }}
                      >
                        {isBullish ? "Bullish" : "Bearish"}
                        {isHidden && (
                          <span style={{ fontSize: "0.7rem", marginLeft: "0.25rem", opacity: 0.7 }}>
                            (Hidden)
                          </span>
                        )}
                      </span>
                      <span
                        style={{
                          display: "inline-block",
                          marginLeft: "0.4rem",
                          padding: "0.25rem 0.55rem",
                          borderRadius: "1rem",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          background: "rgba(15, 23, 42, 0.55)",
                          color: "var(--color-text-muted)",
                          border: "1px solid rgba(148, 163, 184, 0.18)",
                        }}
                      >
                        {divergenceClass}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "0.9rem 1rem",
                        color: strengthColor,
                        fontWeight: "600",
                        textTransform: "capitalize",
                      }}
                    >
                      {item.strength || "-"}
                    </td>
                    <td style={{ padding: "0.9rem 1rem" }}>
                      <span
                        style={{
                          color: zoneColor,
                          fontWeight: "600",
                          fontSize: "0.85rem",
                          textTransform: "capitalize",
                        }}
                      >
                        {item.rsi_zone || "-"}
                      </span>
                    </td>
                    <td style={{ padding: "0.9rem 1rem" }}>
                      <span
                        style={{
                          color: statusColor,
                          fontWeight: "600",
                          fontSize: "0.85rem",
                          textTransform: "capitalize",
                        }}
                      >
                        {item.confirmation_status?.replace("_", " ") || "-"}
                      </span>
                      {item.volume_context && (
                        <span
                          style={{ marginLeft: "0.5rem", fontSize: "0.75rem", color: "var(--color-text-muted)" }}
                        >
                          ({item.volume_context} vol)
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "0.9rem 1rem",
                        color: "var(--color-text-muted)",
                        fontSize: "0.85rem",
                        maxWidth: "260px",
                      }}
                    >
                      {getDivergenceSummary(item)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
            background: "rgba(15, 23, 42, 0.5)",
            borderRadius: "0.75rem",
            border: "1px dashed var(--color-border)",
          }}
        >
          <p style={{ color: "var(--color-text-muted)", fontSize: "1rem", textAlign: "center" }}>
            No RSI divergences match the current filters
          </p>
        </div>
      )}

      {/* RSI Divergence Knowledge Center — collapsible */}
      <div
        style={{
          marginTop: "2.5rem",
          borderRadius: "1rem",
          border: "1px solid var(--color-border)",
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={() => setKnowledgeOpen((o) => !o)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1rem 1.5rem",
            background: "rgba(138, 180, 248, 0.05)",
            border: "none",
            cursor: "pointer",
            color: "var(--color-text)",
            fontSize: "1rem",
            fontWeight: "600",
            textAlign: "left",
          }}
        >
          RSI Divergence Guide
          <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", fontWeight: "400" }}>
            {knowledgeOpen ? "▲ hide" : "▼ show"}
          </span>
        </button>

        {knowledgeOpen && (
          <div style={{ padding: "1.5rem 2rem", background: "rgba(138, 180, 248, 0.03)" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "2rem",
              }}
            >
              <div>
                <h4 style={{ color: "var(--google-green)", marginBottom: "0.75rem", fontSize: "1rem" }}>
                  Bullish Divergence
                </h4>
                <p style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", lineHeight: "1.7", margin: 0 }}>
                  Price makes a <strong>Lower Low</strong>, RSI makes a <strong>Higher Low</strong>. Selling
                  momentum is slowing despite the lower price — often precedes a reversal to the upside.
                </p>
              </div>
              <div>
                <h4 style={{ color: "var(--google-red)", marginBottom: "0.75rem", fontSize: "1rem" }}>
                  Bearish Divergence
                </h4>
                <p style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", lineHeight: "1.7", margin: 0 }}>
                  Price makes a <strong>Higher High</strong>, RSI makes a <strong>Lower High</strong>. Buying
                  momentum is fading at new highs — often signals an upcoming pullback.
                </p>
              </div>
            </div>
            <div
              style={{
                marginTop: "1.5rem",
                padding: "1rem 1.25rem",
                background: "rgba(15, 23, 42, 0.4)",
                borderRadius: "0.75rem",
                fontSize: "0.88rem",
                color: "var(--color-text-muted)",
                borderLeft: "4px solid var(--blue-text)",
                lineHeight: "1.6",
              }}
            >
              <strong style={{ color: "var(--blue-text)", display: "block", marginBottom: "0.25rem" }}>
                Pro Tip
              </strong>
              Divergences are most powerful in Overbought (&gt;70) or Oversold (&lt;30) zones.{" "}
              <em>Confirmed</em> means price already moved in the signal direction.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
